import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  DeviceTabletIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  EmptyState,
  PageHeader,
  StatCard,
  StatusBadge,
  TWButton,
  TWDataTable,
  TWInput,
  TWSelect,
} from '../../components/ui';
import { DataTableSkeleton, StackedText, colTitle, headerCls } from '../../data/columns';
import fleetApi from '../../services/fleetApi';
import AdminOnly from './components/admin-only';
import CommandSheet from './components/command-sheet';
import CompanyStoreSelect from './components/company-store-select';
import DevicesNav from './components/devices-nav';
import { fetchAllDeviceIds } from './fleet-resolve';
import useFleetPoll, { fleetErrorText } from './use-fleet-poll';
import {
  APPS,
  PAGE_SIZE,
  POLL_MS,
  ROLES,
  buildDeviceListQuery,
  relativeSeen,
  statusLabel,
  statusTone,
} from './fleet-utils';

const SORTS = ['store', 'status', 'lastseenat', 'agentversioncode', 'enrolledat'];
const DEBOUNCE_MS = 400;
const DASH = '—';

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const ROLE_TONES = { kiosk: 'blue', kitchen: 'violet', unassigned: 'amber' };

const readParams = (sp) => {
  const sort = sp.get('sort');
  const dir = sp.get('dir');
  const page = parseInt(sp.get('page'), 10);
  return {
    q: sp.get('q') || '',
    role: sp.get('role') || '',
    status: sp.get('status') || '',
    companyid: sp.get('companyid') || '',
    storeid: sp.get('storeid') || '',
    outdated: sp.get('outdated') === '1',
    tag: sp.get('tag') || '',
    page: page > 0 ? page : 1,
    sort: SORTS.includes(sort) ? sort : '',
    dir: dir === 'asc' || dir === 'desc' ? dir : '',
  };
};

const orderbyOf = ({ sort, dir }) => {
  if (dir === 'desc') return 'Desc';
  return sort || dir === 'asc' ? 'Asc' : undefined;
};

// A text box whose value reaches the URL only after the user pauses typing.
// The box keeps what was typed (a trailing space included); only the value
// written to the URL is trimmed, and the box re-syncs from the URL only when
// the URL moved to something other than the trimmed text.
const useDebouncedField = (urlValue, commit) => {
  const [text, setText] = useState(urlValue);
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    setText((current) => (current.trim() === urlValue ? current : urlValue));
  }, [urlValue]);
  useEffect(() => {
    if (text.trim() === urlValue) return undefined;
    const timer = setTimeout(() => commitRef.current(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, urlValue]);
  return [text, setText];
};

const SortableHeader = ({ label, active, desc, onSort, align }) => (
  <button
    type="button"
    onClick={onSort}
    className={`inline-flex w-full select-none items-center gap-1 ${
      align === 'right' ? 'justify-end' : 'justify-start'
    } ${headerCls} cursor-pointer`}
  >
    {label}
    {active ? (
      desc ? (
        <ChevronDownIcon className="h-3 w-3" />
      ) : (
        <ChevronUpIcon className="h-3 w-3" />
      )
    ) : (
      <ChevronUpDownIcon className="h-3 w-3 opacity-40" />
    )}
  </button>
);

const SKELETON_HEADERS = [
  { label: 'Device', w: 'w-40' },
  { label: 'Role', shape: 'pill', w: 'w-20' },
  { label: 'Company › Store', w: 'w-40' },
  { label: 'Status', shape: 'pill', w: 'w-20' },
  { label: 'Apps', w: 'w-24' },
  { label: 'Network', w: 'w-28' },
  { label: 'Uptime', align: 'right', w: 'w-12' },
  { label: 'Pending', align: 'right', w: 'w-8' },
];

const appCell = (device) => {
  const pkg = APPS.find((a) => a.value === device.role)?.packagename;
  const version = pkg ? device.appversions?.[pkg]?.name : null;
  const latest = pkg ? device.latestversions?.[pkg]?.name : null;
  return (
    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
      <p className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {version ? 'v' + version : DASH}
        </span>
        {device.outdated ? (
          <span
            role="img"
            aria-label="Outdated"
            title={latest ? 'latest ' + latest : 'A newer version is available'}
            className="h-2 w-2 rounded-full bg-amber-500"
          />
        ) : null}
      </p>
      <p>{device.agentversionname ? 'Agent v' + device.agentversionname : DASH}</p>
    </div>
  );
};

const Devices = () => (
  <AdminOnly>
    <DevicesList />
  </AdminOnly>
);

const DevicesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = readParams(searchParams);
  const { q, role, status, companyid, storeid, outdated, tag, page, sort, dir } = params;

  const update = useCallback(
    (changes, { resetPage = true } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => {
            const isDefault =
              value === '' ||
              value === null ||
              value === undefined ||
              value === false ||
              (key === 'page' && Number(value) === 1);
            if (isDefault) next.delete(key);
            else next.set(key, value === true ? '1' : String(value));
          });
          if (resetPage && !('page' in changes)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [searchText, setSearchText] = useDebouncedField(q, (value) => update({ q: value }));
  const [tagText, setTagText] = useDebouncedField(tag, (value) => update({ tag: value }));

  // What the list is filtered by, without sort or paging: the toolbar actions
  // resolve the same set the page shows. They read the filters as they are at
  // click time (inside the 400 ms search debounce the URL, and so the sheet's
  // label and ids, still hold the old text).
  const filters = useMemo(
    () => ({
      search: q,
      role,
      status,
      companyid,
      storeid,
      outdated,
      tag,
    }),
    [q, role, status, companyid, storeid, outdated, tag],
  );
  const query = useMemo(
    () =>
      buildDeviceListQuery({
        ...filters,
        page,
        ordercolumn: sort,
        orderby: orderbyOf({ sort, dir }),
      }),
    [filters, page, sort, dir],
  );

  const { data, loading, fetching, error, stalled, reload } = useFleetPoll(
    () => fleetApi.listDevices(query).then((res) => res.data),
    {
      intervalMs: POLL_MS,
      deps: [query],
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load devices')),
    },
  );

  const rows = data?.data || [];
  const totalRecords = data?.totalRecords ?? 0;
  const totals = data?.totals || {};
  const hasFilter = Boolean(q || role || status || companyid || storeid || outdated || tag);

  // The API does a plain Skip/Take: a page past the end answers [] with a
  // positive count. Go back to page 1 instead of showing an empty state.
  const pastTheEnd = !loading && !error && rows.length === 0 && totalRecords > 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) update({ page: 1 }, { resetPage: false });
  }, [pastTheEnd, update]);

  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const [sheet, setSheet] = useState({ open: false, targets: { deviceIds: [], label: '' } });

  // The ids behind the current filter (pages of 100, refused above 300).
  const resolveFilteredIds = async () => {
    setResolving(true);
    try {
      const result = await fetchAllDeviceIds(filters);
      if (!result.ok) {
        message.error(result.error);
        return null;
      }
      if (result.ids.length === 0) {
        message.error('No devices match');
        return null;
      }
      return result.ids;
    } catch (err) {
      message.error(fleetErrorText(err, 'Could not load the devices'));
      return null;
    } finally {
      setResolving(false);
    }
  };

  const openCommandSheet = async () => {
    const ids = await resolveFilteredIds();
    if (!ids) return;
    const label = `${ids.length} ${ids.length === 1 ? 'device' : 'devices'}`;
    setSheet({ open: true, targets: { deviceIds: ids, label } });
  };

  const startRollout = async () => {
    const ids = await resolveFilteredIds();
    if (ids) navigate('/dashboard/devices/rollouts', { state: { deviceids: ids } });
  };

  const onSort = (key) => {
    if (key === 'name') {
      if (sort) update({ sort: '', dir: '' });
      else update({ dir: dir === 'desc' ? '' : 'desc' });
    } else if (sort !== key) {
      update({ sort: key, dir: '' });
    } else if (dir !== 'desc') {
      update({ dir: 'desc' });
    } else {
      update({ sort: '', dir: '' });
    }
  };

  const header = (label, key, align) => (
    <SortableHeader
      label={label}
      align={align}
      active={key === 'name' ? !sort : sort === key}
      desc={dir === 'desc'}
      onSort={() => onSort(key)}
    />
  );

  const columns = [
    {
      title: header('Device', 'name'),
      key: 'device',
      fixed: 'left',
      render: (_, d) => (
        <StackedText
          primary={d.name || d.serialnumber}
          secondary={[d.serialnumber, d.model].filter(Boolean).join(' · ')}
        />
      ),
    },
    {
      title: colTitle('Role'),
      key: 'role',
      render: (_, d) => (
        <StatusBadge size="sm" tone={ROLE_TONES[d.role] || 'slate'} label={statusLabel(d.role)} />
      ),
    },
    {
      title: header('Company › Store', 'store'),
      key: 'store',
      render: (_, d) =>
        d.store ? (
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {d.store.company ? (
              <>
                <Link
                  to={`/dashboard/companies/${d.store.company.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-blue-600 hover:underline dark:text-sky-400"
                >
                  {d.store.company.name}
                </Link>{' '}
                <span className="text-slate-400">›</span>{' '}
              </>
            ) : null}
            {d.store.name}
          </span>
        ) : (
          DASH
        ),
    },
    {
      title: header('Status', 'status'),
      key: 'status',
      render: (_, d) => (
        <div className="space-y-1">
          <StatusBadge dot size="sm" tone={statusTone(d.status)} label={statusLabel(d.status)} />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {d.lastseenat ? 'Seen ' + relativeSeen(d.lastseenat) : DASH}
          </p>
        </div>
      ),
    },
    {
      title: header('Apps', 'agentversioncode'),
      key: 'apps',
      render: (_, d) => appCell(d),
    },
    {
      title: colTitle('Network'),
      key: 'network',
      render: (_, d) =>
        d.telemetry ? (
          <StackedText primary={d.telemetry.wifissid || DASH} secondary={d.telemetry.ip} />
        ) : (
          DASH
        ),
    },
    {
      title: colTitle('Uptime', { align: 'right' }),
      key: 'uptime',
      align: 'right',
      render: (_, d) =>
        d.telemetry?.uptimeseconds != null ? (
          <span className="tabular-nums">{Math.round(d.telemetry.uptimeseconds / 3600) + ' h'}</span>
        ) : (
          DASH
        ),
    },
    {
      title: colTitle('Pending', { align: 'right' }),
      key: 'pending',
      align: 'right',
      render: (_, d) =>
        d.pendingcommands > 0 ? (
          <StatusBadge size="sm" tone="sky" label={String(d.pendingcommands)} />
        ) : (
          DASH
        ),
    },
  ];

  const cards = [
    { label: 'Total', value: totals.total, className: '' },
    {
      label: 'Online',
      value: totals.online,
      valueClassName: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Offline',
      value: totals.offline,
      valueClassName: totals.offline > 0 ? 'text-rose-600 dark:text-rose-400' : '',
    },
    {
      label: 'Outdated',
      value: totals.outdated,
      valueClassName: totals.outdated > 0 ? 'text-amber-600 dark:text-amber-400' : '',
      onClick: () => update({ outdated: true }),
    },
    {
      label: 'Awaiting assignment',
      value: totals.awaiting,
      valueClassName: totals.awaiting > 0 ? 'text-amber-600 dark:text-amber-400' : '',
      className: 'col-span-2 lg:col-span-1',
      onClick: () => update({ role: 'unassigned' }),
    },
  ];

  const clearFilters = () =>
    update({ q: '', role: '', status: '', companyid: '', storeid: '', outdated: false, tag: '' });

  let body;
  if (error) {
    body = (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load devices"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  } else if (loading) {
    body = <DataTableSkeleton headers={SKELETON_HEADERS} rows={10} />;
  } else if (totalRecords === 0) {
    body = hasFilter ? (
      <EmptyState
        icon={DeviceTabletIcon}
        title="No devices match"
        description="Try removing a filter."
        action={
          <TWButton variant="ghost" onClick={clearFilters}>
            Clear filters
          </TWButton>
        }
      />
    ) : (
      <EmptyState
        icon={DeviceTabletIcon}
        title="No devices yet"
        description="Enroll a tablet with a token and it appears here."
        action={
          <Link
            to="/dashboard/devices/enroll"
            className="inline-flex h-9 items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:bg-sky-500 dark:text-slate-900 dark:hover:bg-sky-400 dark:focus:ring-sky-400 dark:focus:ring-offset-slate-900"
          >
            Create enrollment token
          </Link>
        }
      />
    );
  } else if (pastTheEnd) {
    body = <DataTableSkeleton headers={SKELETON_HEADERS} rows={10} />;
  } else {
    body = (
      <div className="reveal">
        {stalled ? (
          <div
            role="status"
            className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          >
            <span>Live updates paused.</span>
            <TWButton variant="link" size="sm" onClick={reload}>
              Retry
            </TWButton>
          </div>
        ) : null}
        <TWDataTable
          columns={columns}
          data={rows}
          loading={fetching}
          rowClickPath={(d) => `/dashboard/devices/${d.id}`}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: totalRecords,
            showSizeChanger: false,
            showTotal: (t) => `${t} ${t === 1 ? 'device' : 'devices'}`,
            onChange: (next) => update({ page: next }, { resetPage: false }),
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title="Devices"
          description="Tablets enrolled in the Thisway fleet."
          actions={
            <>
              <TWButton
                variant="default"
                onClick={startRollout}
                disabled={totalRecords === 0 || resolving}
              >
                New rollout
              </TWButton>
              <TWButton
                variant="primary"
                onClick={openCommandSheet}
                disabled={totalRecords === 0 || resolving}
              >
                Send command…
              </TWButton>
            </>
          }
        />
        <DevicesNav current="devices" />

        {error ? null : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {cards.map(({ label, value, ...rest }) => (
              <StatCard key={label} label={label} value={value ?? 0} loading={loading} {...rest} />
            ))}
          </div>
        )}

        <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/70 sm:p-5">
          <div className="grid grid-cols-6 items-end gap-3">
            <div className="col-span-6 sm:col-span-2">
              <TWInput
                label="Search"
                leftIcon={MagnifyingGlassIcon}
                placeholder="Search name, serial, store or company"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-span-6 sm:col-span-1">
              <TWSelect
                label="Role"
                placeholder="Any role"
                options={ROLES}
                value={role || undefined}
                onChange={(value) => update({ role: value || '' })}
              />
            </div>
            <div className="col-span-6 sm:col-span-1">
              <TWSelect
                label="Status"
                placeholder="Any status"
                options={STATUS_OPTIONS}
                value={status || undefined}
                onChange={(value) => update({ status: value || '' })}
              />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <TWInput
                label="Tag"
                placeholder="e.g. front-counter"
                value={tagText}
                onChange={(e) => setTagText(e.target.value)}
              />
            </div>
            <div className="col-span-6 sm:col-span-4">
              <CompanyStoreSelect
                companyId={companyid}
                storeId={storeid}
                onChange={({ companyid: c, storeid: s }) => update({ companyid: c, storeid: s })}
              />
            </div>
            <div className="col-span-6 flex items-center gap-2 sm:col-span-2">
              <TWButton
                variant="subtle"
                aria-pressed={outdated}
                className={
                  outdated
                    ? '!bg-blue-100 !text-blue-800 dark:!bg-sky-500/20 dark:!text-sky-300'
                    : ''
                }
                onClick={() => update({ outdated: !outdated })}
              >
                Outdated only
              </TWButton>
              {hasFilter ? (
                <TWButton variant="ghost" onClick={clearFilters}>
                  Clear filters
                </TWButton>
              ) : null}
            </div>
          </div>
        </div>

        {body}
      </div>

      <CommandSheet
        open={sheet.open}
        targets={sheet.targets}
        onClose={() => setSheet((prev) => ({ ...prev, open: false }))}
        onSent={reload}
      />
    </div>
  );
};

export default Devices;
